import React, { useLayoutEffect, useRef } from "react"
import {
    FISH_TAG_BADGE_SIZE_PX,
    FISH_TAG_CIRCLE_FONT_SIZE_PX,
    FISH_TAG_PILL_FONT_SIZE_PX,
    formatFishTagCommaJoin,
    formatFishTagPillText,
    isLongFishTag,
    splitBoxLabelFishTags,
} from "./fishTagBadges.js"
import "./fish-tag-badges.css"

function FishTagCircle({ tag }) {
    const clean = String(tag ?? "").trim()
    if (!clean) {
        return null
    }

    const sizePx = FISH_TAG_BADGE_SIZE_PX

    return (
        <div
            className="bl-tag-circle"
            style={{
                width: `${sizePx}px`,
                height: `${sizePx}px`,
                minWidth: `${sizePx}px`,
                minHeight: `${sizePx}px`,
                maxWidth: `${sizePx}px`,
                maxHeight: `${sizePx}px`,
            }}
        >
            <span
                className="bl-tag-circle-text"
                style={{ fontSize: `${FISH_TAG_CIRCLE_FONT_SIZE_PX}px` }}
            >
                {clean}
            </span>
        </div>
    )
}

function FishTagPill({ tag, fluid = false, title = undefined }) {
    const displayText = fluid ? String(tag ?? "").trim() : formatFishTagPillText(tag)
    if (!displayText) {
        return null
    }

    const sizePx = FISH_TAG_BADGE_SIZE_PX

    return (
        <div
            className={`bl-tag-pill${fluid ? " bl-tag-pill--fluid" : ""}`.trim()}
            title={title ?? (fluid ? displayText : undefined)}
            style={{
                minHeight: `${sizePx}px`,
                height: `${sizePx}px`,
                maxHeight: `${sizePx}px`,
            }}
        >
            <span className="bl-tag-pill-text" style={{ fontSize: `${FISH_TAG_PILL_FONT_SIZE_PX}px` }}>
                {displayText}
            </span>
        </div>
    )
}

function renderTagItems(tags) {
    return tags.map((tag, index) => (
        isLongFishTag(tag) ? (
            <FishTagPill key={`${tag}-${index}`} tag={tag} />
        ) : (
            <FishTagCircle key={`${tag}-${index}`} tag={tag} />
        )
    ))
}

/**
 * @param {{
 *   fishTag?: string | null,
 *   className?: string,
 *   collapseWhenOverflow?: boolean,
 *   remeasureKey?: number,
 * }} props
 */
export default function FishTagBadgeGroup({
    fishTag,
    className = "",
    collapseWhenOverflow = false,
    remeasureKey = 0,
}) {
    const tags = splitBoxLabelFishTags(fishTag)
    const fitRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const measureRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const circlesRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const pillRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const useCommaPillRef = useRef(false)
    const fishTagKeyRef = useRef(fishTag)

    useLayoutEffect(() => {
        if (!collapseWhenOverflow) {
            useCommaPillRef.current = false
            circlesRef.current?.classList.remove("bl-tag-group-fit-hidden")
            circlesRef.current?.classList.add("bl-tag-group-fit-visible")
            pillRef.current?.classList.add("bl-tag-group-fit-hidden")
            pillRef.current?.classList.remove("bl-tag-group-fit-visible")
            return
        }

        const fitEl = fitRef.current
        const measureEl = measureRef.current
        const circlesEl = circlesRef.current
        const pillEl = pillRef.current
        if (!fitEl || !measureEl || !circlesEl || !pillEl) {
            return
        }

        if (fishTagKeyRef.current !== fishTag) {
            fishTagKeyRef.current = fishTag
            useCommaPillRef.current = false
        }

        const available = fitEl.clientWidth
        if (available <= 0) {
            return
        }

        const needed = measureEl.offsetWidth
        const overflowPx = needed - available

        const prev = useCommaPillRef.current
        const next = prev ? (overflowPx > -10) : (overflowPx > 2)

        if (next === prev) {
            return
        }

        useCommaPillRef.current = next
        circlesEl.classList.toggle("bl-tag-group-fit-hidden", next)
        circlesEl.classList.toggle("bl-tag-group-fit-visible", !next)
        pillEl.classList.toggle("bl-tag-group-fit-hidden", !next)
        pillEl.classList.toggle("bl-tag-group-fit-visible", next)
    }, [collapseWhenOverflow, fishTag, remeasureKey])

    if (tags.length === 0) {
        return null
    }

    const groupClassName = [
        "bl-tag-group",
        collapseWhenOverflow ? "bl-tag-group--single-row" : "",
        className,
    ].filter(Boolean).join(" ")

    const tagItems = renderTagItems(tags)
    const commaPillText = formatFishTagCommaJoin(tags)

    if (!collapseWhenOverflow) {
        return (
            <div
                className={groupClassName}
                style={{ "--bl-tag-badge-size": `${FISH_TAG_BADGE_SIZE_PX}px` }}
            >
                {tagItems}
            </div>
        )
    }

    return (
        <div
            ref={fitRef}
            className="bl-tag-group-fit"
            style={{ "--bl-tag-badge-size": `${FISH_TAG_BADGE_SIZE_PX}px` }}
        >
            <div
                ref={measureRef}
                className={`bl-tag-group bl-tag-group--measure ${className}`.trim()}
                style={{ "--bl-tag-badge-size": `${FISH_TAG_BADGE_SIZE_PX}px` }}
                aria-hidden="true"
            >
                {tagItems}
            </div>
            <div
                ref={circlesRef}
                className={`${groupClassName} bl-tag-group-fit-visible`.trim()}
                style={{ "--bl-tag-badge-size": `${FISH_TAG_BADGE_SIZE_PX}px` }}
            >
                {tagItems}
            </div>
            <div
                ref={pillRef}
                className={`${groupClassName} bl-tag-group-fit-hidden`.trim()}
                style={{ "--bl-tag-badge-size": `${FISH_TAG_BADGE_SIZE_PX}px` }}
            >
                <FishTagPill tag={commaPillText} fluid />
            </div>
        </div>
    )
}
